/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from "@angular/common";
import {Component, OnInit} from '@angular/core';
import {FabMessageService} from "@fabric/components/message";
import {
  PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG, INDUSTRY_COLDEF} from "../../../common/constants";
import {range, sortIndustryScenarios} from "../../../core/helpers/generic-helpers";
import {StateService} from "../../../core/state/state.service";
import {GlobalScenario} from "../../../models/global-scenario-dto.model";
import {IndustryScenario} from "../../../models/industry-scenario-dto.model";
import {ExcelExportService} from "../../../services/excel-export.service";
import {IndustryScenarioService} from "../../../services/industry-scenario.service";

@Component({
    selector: 'app-industry-consolidation-report',
    templateUrl: './industry-consolidation-report.component.html',
    styleUrl: './industry-consolidation-report.component.scss',
    standalone: false
})
export class IndustryConsolidationReportComponent implements OnInit {
  protected readonly INDUSTRY_COLDEF = INDUSTRY_COLDEF;

  showSpinner: boolean;
  aggregateTos: boolean = true;
  disableAggregateTos: boolean = false;
  showHiddenScenarios: boolean = false;
  selectedSourceType: string = 'fcorg';

  availableScenarios: any[] = [];
  displayedAvailableScenarios: any[] = [];
  selectedIndustryScenarios: IndustryScenario[] = [];
  selectedGlobalScenario: GlobalScenario;
  memberScenarios: any[] = [];
  memberScenariosToRemove: any = [];

  yearRange: number[];
  startYear: number;
  endYear: number;


  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<IndustryScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.forecastOrganization : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              readonly industryScenarioService: IndustryScenarioService,
              public stateService: StateService) {

  }

  ngOnInit(): void {
    this.getYearRange();
    this.onSourceChange();
  }

  /**
   * Get list of Industry scenarios that the user has access to
   */
  getIndustryScenarios(): void {
    this.showSpinner = true;
    this.industryScenarioService.getIndustryScenarioByGmid(this.stateService.getUserGmid()).subscribe({
      next: (data) => {
        this.availableScenarios = data;
        this.displayedAvailableScenarios = data;
        if (!this.showHiddenScenarios) {
          this.displayedAvailableScenarios = this.availableScenarios.filter(row => !row.isHidden);
        }
      },
      error: (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.showSpinner = false;
        // If they are on the By TVP Scenarios tab, filter only to show TVP scenarios,
        // otherwise show the list with the TVP scenarios filtered out
        if (this.selectedSourceType === 'tvp') {
          this.displayedAvailableScenarios = this.displayedAvailableScenarios.filter(row => row.forecastOrganization.includes('TVP'));
          this.availableScenarios = this.availableScenarios.filter(row => row.forecastOrganization.includes('TVP'));
        } else {
          this.displayedAvailableScenarios = this.displayedAvailableScenarios.filter(row => !row.forecastOrganization.includes('TVP'));
          this.availableScenarios = this.availableScenarios.filter(row => !row.forecastOrganization.includes('TVP'));
        }
      }
    })
  }

  /**
   * Sets the scenario as selected
   * @param event the clicked scenario row
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedIndustryScenarios.push(event.data);
    } else {
      this.selectedIndustryScenarios = this.selectedIndustryScenarios.filter(element => element != event.data);
    }
  }

  /**
   * Calls the proper function to fetch the member scenarios based on the selected scenario
   * @param event The event generated by the child
   */
  onSelectGlobal(event: any) {
    this.memberScenarios = event.memberScenarios
    this.selectedGlobalScenario = event.globalScenario;
  }

  /**
   * Sets the member scenario as selected
   * @param event the clicked member scenario row event
   */
  onMemberSelect(event): void {
    if (event.node.selected) {
      this.memberScenariosToRemove.push(event.data);
    } else {
      this.memberScenariosToRemove = this.memberScenariosToRemove.filter(element => element != event.data);
    }
  }

  /**
   * When a user adds an available industry scenario to the selected member scenarios
   */
  onAddMemberScenario(): void {
    this.memberScenarios = sortIndustryScenarios(this.memberScenarios.concat(this.selectedIndustryScenarios));
    this.availableScenarios = this.availableScenarios.filter(row => !this.selectedIndustryScenarios.includes(row));

    // Clear out the selected industry scenarios
    this.selectedIndustryScenarios = [];

    this.setAvailableScenariosTables();
  }

  /**
   * Whenever use changes between FCORG and Global scenarios, reset variables and fetch the proper scenarios
   */
  onSourceChange(): void {
    this.selectedIndustryScenarios = [];
    this.selectedGlobalScenario = undefined;
    this.memberScenarios = [];
    this.memberScenariosToRemove = [];
    if (this.selectedSourceType !== 'global') {
      this.getIndustryScenarios();
    }
    if (this.selectedSourceType === 'tvp') {
      this.aggregateTos = false;
      this.disableAggregateTos = true;
    } else {
      this.aggregateTos = true;
      this.disableAggregateTos = false;
    }
  }

  /**
   * When the user removes an already selected member scenario back to the available scenarios
   */
  onRemoveMemberScenario(): void {
    if (this.memberScenariosToRemove.length > 0) {
      // Update the data used in the tables
      this.availableScenarios = sortIndustryScenarios(this.availableScenarios.concat(this.memberScenariosToRemove));
      this.memberScenarios = this.memberScenarios
      .filter((scenario) => !this.memberScenariosToRemove.includes(scenario));

      // Clear out the selected member scenarios to remove
      this.memberScenariosToRemove = [];
      this.selectedIndustryScenarios = [];

      this.setAvailableScenariosTables();
    }
  }

  /**
   * Export the report
   */
  onRunReport(): void {
    this.showSpinner = true;
    const industryScenarioIds = this.memberScenarios.map(row => row.id).join(', ');
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const idList = this.memberScenarios.map(row => row.id);
    let scenarioString: string;
    if (this.selectedSourceType == 'global') {
      scenarioString = this.selectedGlobalScenario?.id.toString();
    } else {
      scenarioString = idList.length > 1 ? idList.sort()[0] + "-" + idList.sort()[idList.length - 1] : idList[0]?.toString();
    }
    const fileName = 'IndustryConsolidation_' + scenarioString + "_" + currDate;
    this.industryScenarioService.getConsolidationReport(industryScenarioIds, this.startYear, this.endYear,
      this.stateService.getUserGmid(), this.aggregateTos, 'All').subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);
      });
  }

  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const currentYear = new Date().getFullYear()
    // This report has a custom year range so that users can go back to an older year
    this.yearRange = range(1990, currentYear + 17);
    this.startYear = currentYear - 2;
    this.endYear = currentYear + 9;
  }

  /**
   * Sets the available scenarios tables based on the current state of the showHiddenScenarios flag.
   */
  setAvailableScenariosTables(): void {
    this.displayedAvailableScenarios = this.availableScenarios;
    if (!this.showHiddenScenarios) {
      this.displayedAvailableScenarios = this.availableScenarios.filter(row => row.isHidden === false);
    }
  }
}
